import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { shouldReloadModuleFailure } from "@/lib/moduleLoadRecovery";

const STORAGE_KEY = "module-load-recovery-at";
const copy: Record<string, [string, string]> = {
  tr: ["Sayfa yüklenemedi", "Yeniden yükle"],
  en: ["Page could not load", "Reload"],
  nl: ["De pagina kon niet laden", "Opnieuw laden"],
  fr: ["La page n’a pas pu se charger", "Recharger"],
  ar: ["تعذر تحميل الصفحة", "إعادة التحميل"],
};

export class ModuleLoadBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    // A deployment or interrupted request can invalidate a cached lazy module.
    // Reload the document once, never loop or erase the visitor's stored data.
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      const timestamp = raw === null ? null : Number(raw);
      const lastReload = timestamp !== null && Number.isFinite(timestamp) ? timestamp : null;
      const now = Date.now();
      if (shouldReloadModuleFailure(error, lastReload, now)) {
        sessionStorage.setItem(STORAGE_KEY, String(now));
        window.location.reload();
      }
    } catch {
      // Blocked storage must not cause a reload loop or another render failure.
    }
  }

  render() {
    if (!this.state.failed) return this.props.children;
    const language = window.location.pathname.match(/^\/(tr|nl|fr|ar)(?:\/|$)/)?.[1] || "en";
    const [title, reload] = copy[language] || copy.en;
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-6 bg-background p-6 text-foreground" dir={language === "ar" ? "rtl" : "ltr"}>
        <h1 className="text-2xl font-semibold">{title}</h1>
        <Button onClick={() => window.location.reload()}>{reload}</Button>
      </main>
    );
  }
}